import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { activitiesAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSelector } from '@/components/LanguageSelector';
import { toast } from 'sonner';
import { Loader2, User, Briefcase, Gift, Heart, ShieldCheck } from 'lucide-react';

const USER_TYPES = [
  { value: 'user', label: 'Pessoa em Recuperação', sublabel: 'Busco apoio para minha jornada', icon: User, color: 'purple' },
  { value: 'family', label: 'Familiar / Cuidador', sublabel: 'Apoio alguém em recuperação', icon: Heart, color: 'rose' },
  { value: 'professional', label: 'Profissional de Saúde', sublabel: 'Psicólogo, Terapeuta, Médico', icon: Briefcase, color: 'blue' },
];

export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, register, isAuthenticated, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const referralCode = searchParams.get('ref') || '';
  const tabParam = searchParams.get('tab') || '';
  const fromQuiz = searchParams.get('from') === 'quiz';

  // Read quiz data from localStorage
  const quizRole = localStorage.getItem('uclean_quiz_role') || '';
  const quizResultRaw = localStorage.getItem('uclean_quiz_result');
  const quizResult = quizResultRaw ? JSON.parse(quizResultRaw) : null;

  const [loginData, setLoginData] = useState({ email: '', password: '', remember_me: false });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    user_type: quizRole || '',
    professional_type: '',
    professional_registration: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/feed', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Pre-fill from quiz
  useEffect(() => {
    if (quizRole && !registerData.user_type) {
      setRegisterData(prev => ({ ...prev, user_type: quizRole }));
    }
  }, [quizRole]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password, loginData.remember_me);
      toast.success(t('auth.loginSuccess'));
      navigate('/feed', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (referralCode) localStorage.setItem('uclean_pending_ref', referralCode);
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.user_type) {
      toast.error('Selecione como deseja usar a U-Clean');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Aceite os Termos de Uso e Política de Privacidade');
      return;
    }

    setIsLoading(true);
    try {
      // Map family → user for backend (backend only knows user/professional)
      const backendType = registerData.user_type === 'family' ? 'user' : registerData.user_type;
      const userData = await register(
        registerData.email,
        registerData.password,
        registerData.name,
        backendType,
        referralCode
      );

      // If came from quiz, award tokens
      if (quizResult) {
        try {
          await activitiesAPI.completeQuiz({
            role_type: registerData.user_type,
            level: quizResult.level || 'leve',
            score: quizResult.score || 0,
            quiz_type: quizResult.quiz_type || 'recuperacao',
          });
        } catch (err) {
          // Silently fail - tokens are bonus
        }
        localStorage.removeItem('uclean_quiz_role');
        localStorage.removeItem('uclean_quiz_result');
      }

      toast.success(t('auth.registerSuccess'));
      navigate('/feed', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const defaultTab = (referralCode || tabParam === 'register' || fromQuiz) ? 'register' : 'login';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #111133 50%, #0a0a1a 100%)' }}>
      {/* Language selector - top right */}
      <div className="fixed top-4 right-4 z-50"><LanguageSelector /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">U-Clean</h1>
          <p className="text-gray-400">{t('app.tagline')}</p>
        </div>

        {/* Quiz result banner */}
        {fromQuiz && quizResult && (
          <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)' }} data-testid="quiz-result-banner">
            <ShieldCheck className="h-5 w-5 text-purple-400 shrink-0" />
            <div>
              <p className="text-sm text-purple-300 font-medium">Quiz concluído!</p>
              <p className="text-xs text-gray-400">Crie sua conta para receber +100 AWA e iniciar sua jornada.</p>
            </div>
          </div>
        )}

        <Card className="border-white/10 bg-white/5 backdrop-blur-md shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white">{t('auth.welcome')}</CardTitle>
            <CardDescription className="text-gray-400">Entre ou crie sua conta para continuar</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={defaultTab}>
              <TabsList className="grid w-full grid-cols-2 bg-white/5">
                <TabsTrigger value="login" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-gray-400">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-gray-400">{t('auth.register')}</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-gray-300">{t('auth.email')}</Label>
                    <Input id="login-email" type="email" placeholder="seu@email.com" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required disabled={isLoading} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="login-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-gray-300">{t('auth.password')}</Label>
                    <Input id="login-password" type="password" placeholder="********" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required disabled={isLoading} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="login-password-input" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="remember-me" checked={loginData.remember_me} onCheckedChange={(v) => setLoginData({ ...loginData, remember_me: !!v })} className="border-white/20" data-testid="remember-me-checkbox" />
                      <Label htmlFor="remember-me" className="text-sm text-gray-400 cursor-pointer">Lembrar de mim</Label>
                    </div>
                    <Link to="/esqueci-senha" className="text-sm text-purple-400 hover:underline" data-testid="forgot-password-link">Esqueceu a senha?</Link>
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={isLoading} data-testid="login-submit-button">
                    {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Entrando...</> : t('auth.login')}
                  </Button>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-transparent px-2 text-gray-500">ou</span></div>
                  </div>
                  <Button type="button" variant="outline" className="w-full border-white/10 text-gray-300 hover:bg-white/5 hover:text-white gap-2" onClick={handleGoogleLogin} disabled={isLoading} data-testid="google-login-button">
                    <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    Entrar com Google
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  {referralCode && (
                    <div className="p-3 rounded-lg flex items-center gap-2" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }} data-testid="referral-banner">
                      <Gift className="h-5 w-5 text-green-400 shrink-0" />
                      <p className="text-sm text-green-300">Você foi convidado! Cadastre-se e ganhe benefícios.</p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="register-name" className="text-gray-300">{t('auth.name')}</Label>
                    <Input id="register-name" type="text" placeholder="Seu nome" value={registerData.name} onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })} required disabled={isLoading} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="register-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="register-email" className="text-gray-300">{t('auth.email')}</Label>
                    <Input id="register-email" type="email" placeholder="seu@email.com" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required disabled={isLoading} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="register-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="register-password" className="text-gray-300">{t('auth.password')}</Label>
                    <Input id="register-password" type="password" placeholder="********" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} required minLength={6} disabled={isLoading} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="register-password-input" />
                  </div>

                  {/* User type selection - 3 options */}
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Como deseja usar a U-Clean? <span className="text-red-400">*</span></Label>
                    <div className="space-y-2">
                      {USER_TYPES.map((ut) => {
                        const Icon = ut.icon;
                        const isActive = registerData.user_type === ut.value;
                        const colors = {
                          purple: { border: 'border-purple-500', bg: 'bg-purple-500/10', text: 'text-purple-400' },
                          rose: { border: 'border-rose-500', bg: 'bg-rose-500/10', text: 'text-rose-400' },
                          blue: { border: 'border-blue-500', bg: 'bg-blue-500/10', text: 'text-blue-400' },
                        }[ut.color];
                        return (
                          <button
                            key={ut.value}
                            type="button"
                            onClick={() => setRegisterData({ ...registerData, user_type: ut.value })}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                              isActive ? `${colors.border} ${colors.bg}` : 'border-white/10 hover:border-white/20'
                            }`}
                            data-testid={`user-type-${ut.value}`}
                          >
                            <Icon className={`h-5 w-5 ${isActive ? colors.text : 'text-gray-500'}`} />
                            <div>
                              <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>{ut.label}</p>
                              <p className="text-xs text-gray-500">{ut.sublabel}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Professional fields */}
                  {registerData.user_type === 'professional' && (
                    <div className="space-y-3 p-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }} data-testid="professional-fields">
                      <div>
                        <Label className="text-gray-300 text-sm">Especialidade</Label>
                        <select
                          value={registerData.professional_type}
                          onChange={(e) => setRegisterData({ ...registerData, professional_type: e.target.value })}
                          className="w-full mt-1 p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                          data-testid="professional-type-select"
                        >
                          <option value="" className="text-gray-900">Selecione...</option>
                          <option value="psicologo" className="text-gray-900">Psicólogo(a)</option>
                          <option value="psiquiatra" className="text-gray-900">Psiquiatra</option>
                          <option value="terapeuta" className="text-gray-900">Terapeuta</option>
                          <option value="educador_fisico" className="text-gray-900">Educador(a) Físico</option>
                          <option value="massoterapeuta" className="text-gray-900">Massoterapeuta</option>
                          <option value="assistente_social" className="text-gray-900">Assistente Social</option>
                          <option value="outro" className="text-gray-900">Outro</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-300 text-sm">Registro Profissional (CRP/CRM/CRT)</Label>
                        <Input
                          placeholder="Ex: CRP 06/123456"
                          value={registerData.professional_registration}
                          onChange={(e) => setRegisterData({ ...registerData, professional_registration: e.target.value })}
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                          data-testid="professional-registration-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Opcional no momento. Poderá ser verificado futuramente.</p>
                      </div>
                    </div>
                  )}

                  {/* Terms */}
                  <div className="flex items-start space-x-2 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <Checkbox id="accept-terms" checked={acceptedTerms} onCheckedChange={setAcceptedTerms} className="mt-0.5 border-white/20" />
                    <Label htmlFor="accept-terms" className="text-sm text-gray-400 leading-relaxed cursor-pointer">
                      Li e aceito os{' '}
                      <Link to="/termos" target="_blank" className="text-purple-400 hover:underline">Termos de Uso</Link>
                      {' '}e a{' '}
                      <Link to="/privacidade" target="_blank" className="text-purple-400 hover:underline">Política de Privacidade</Link>
                    </Label>
                  </div>

                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={isLoading || !registerData.user_type || !acceptedTerms} data-testid="register-submit-button">
                    {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Criando conta...</> : 'Criar Conta'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
            <Link to="/termos" className="hover:text-gray-300 hover:underline">Termos de Uso</Link>
            <span>·</span>
            <Link to="/privacidade" className="hover:text-gray-300 hover:underline">Política de Privacidade</Link>
            <span>·</span>
            <Link to="/aviso-importante" className="hover:text-amber-400 hover:underline text-amber-500/60">Aviso Importante</Link>
          </div>
          <p className="mt-3 text-xs text-gray-600">© {new Date().getFullYear()} U-Clean. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
}
